import ContactSearch from "./contact-search";
import { formatContactPhone } from "./presentation";
import {
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useCookies } from "react-cookie";
import { useLocation, useHistory, useParams } from "react-router-dom";
import UIkit from "uikit";
import Papa from "papaparse";
import classnames from "classnames";
import feather from "feather-icons";
import { AppContexts } from "../../providers";
import { useDeepCompareEffect } from "../../hook";
import Pagination from "../common/Pagination";
import RecordsPerPage from "../common/RecordsPerPage";
import TableLoading from "../common/Loading/table_loading";
import SearchableDropdown from "../common/SearchableDropdown";
import { tagOptionsByGroup } from "../../utils/tag-groups";
import { Link } from "../../ui";
import CRMDetail from "./detail";
import ContactAdd from "../contacts/add";
import ContactDelete from "../contacts/delete";
import ContactMessage from "../contacts/message";
import TagsList from "../common/Rentals/TagsList";
import CRMActivityAdd from "./activity-add";
import DeleteRentalDialog from "../common/Rentals/DeleteRentalDialog";
import RentalFormModal from "../common/Rentals/RentalFormModal";
import { usePermissionsStore } from "../permissions/permissions_store";
import { buildCrmDetailPath, normalizeCrmSubpage } from "./route-utils";

const ADD_UNIT_CRM_CONFIG = {
  showDestinationField: false,
  showLatLng: true,
  showGuideName: false,
  title: "Property",
  defaultDestinationId: 0,
  showLogs: false,
  messageOnAdd: "Added Successfully",
  messageOnEdit: "Updated Successfully",
  redirectPath: "/crm/properties",
};

const CONTACT_IMPORT_CATEGORIES = new Set([
  "owner",
  "maintenance",
  "vendor",
  "other",
  "leads",
  "housekeeper",
  "guest",
]);

const FeatherIcon = ({ name, size = 16, className = "" }) => {
  const icon = feather.icons[name];
  if (!icon) return null;
  return (
    <svg
      className={`feather-icon ${className}`}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      dangerouslySetInnerHTML={{ __html: icon.contents }}
    />
  );
};

// ─── Left-nav structure ───
const NAV_ITEMS = [
  { key: "pipelines", slug: "pipelines", label: "Pipelines", path: "/crm/pipelines", separator: true },
  { key: "owner", slug: "owners", label: "Owners" },
  { key: "guest", slug: "guests", label: "Guests" },
  { key: "unit", slug: "properties", label: "Properties", separator: true },
  { key: "leads", slug: "leads", label: "Leads" },
  { key: "housekeeper", slug: "housekeepers", label: "Housekeepers" },
  { key: "maintenance", slug: "maintenance", label: "Maintenance" },
  { key: "vendor", slug: "vendors", label: "Vendors" },
  { key: "other", slug: "other", label: "Other" },
];

const SLUG_TO_KEY = NAV_ITEMS.reduce((acc, n) => {
  acc[n.slug] = n.key;
  return acc;
}, { units: "unit" });
const KEY_TO_SLUG = NAV_ITEMS.reduce((acc, n) => {
  acc[n.key] = n.slug;
  return acc;
}, {});

// Shareable per-row id used in the URL (`/crm/<slug>/<id>`).
// People rows expose a `crm_identity_id`; units fall back to `rental_id`.
const getShareId = (item) => {
  if (!item) return null;
  if (item._type === "unit") return item.rental_id || item.id || null;
  return item.crm_identity_id || item.id || item.guest_id || item.contact_id || null;
};

const LEAD_FILTERS = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  { key: "contacted", label: "Contacted" },
  { key: "qualified", label: "Qualified" },
  { key: "proposal_sent", label: "Proposal Sent" },
  { key: "negotiation", label: "Negotiation" },
  { key: "contract_sent", label: "Contract Sent" },
  { key: "won", label: "Won" },
  { key: "lost", label: "Lost" },
  { key: "nurture", label: "Nurture" },
];

// ─── Sort options ───
const sortOptions = [
  { key: "updated_at", title: "Last Updated" },
  { key: "first_name", title: "First Name" },
  { key: "last_name", title: "Last Name" },
];

const unitSortOptions = [
  { key: "updated_at", title: "Last Updated" },
  { key: "address", title: "Address" },
  { key: "pms_id", title: "PMS ID" },
];

const defaultCriteria = {
  search_term: "",
  sort: "updated_at",
  category: "",
  status: "",
  property: "",
  tag: "",
  per: "20",
  page: 0,
};

const parseQueryString = (queryString) => {
  return {
    ...defaultCriteria,
    ...Object.fromEntries(new URLSearchParams(queryString)),
  };
};

const getInitials = (item) => {
  if (item?.pms_id) return (item.pms_id || "U")[0].toUpperCase();
  const first = (item?.first_name || "")[0] || "";
  const last = (item?.last_name || "")[0] || "";
  return (first + last).toUpperCase() || "?";
};

const CRM = () => {
  const { canEdit } = usePermissionsStore();
  const location = useLocation();
  const history = useHistory();
  const { category: categorySlug, id: idParam, subpage } = useParams();
  const activeSubpage = normalizeCrmSubpage(subpage);
  const initialCategoryFromPath = categorySlug
    ? (SLUG_TO_KEY[categorySlug] || "owner")
    : null;
  const [criteria, setCriteria] = useState(() => {
    const base = location.search ? parseQueryString(location.search) : { ...defaultCriteria };
    const cat = initialCategoryFromPath
      || base.category
      || "owner";
    return { ...base, category: cat };
  });
  const [selectedFlags, setSelectedFlags] = useState([]);

  const crmContext = useContext(AppContexts.CRMContext);
  const contactContext = useContext(AppContexts.ContactsContext);
  const settingsContext = useContext(AppContexts.SettingsContext);
  const rentalContext = useContext(AppContexts.RentalsContext);
  const { user } = useContext(AppContexts.AuthContext);
  const [cookies] = useCookies(["records-per-page"]);
  const { "records-per-page": recordsPerPage = 20 } = cookies;

  const importRef = useRef(null);

  const {
    listContacts,
    totalCnt,
    flagIsProcessing,
    contact: selectedContact,
    setContact,
    counts,
    activeFilter,
    setActiveFilter,
    statusFilter,
    setStatusFilter,
    selectedContacts,
    setSelectedContacts,
    setBatchFlag,
    updateContact,
  } = crmContext;

  useEffect(() => {
    document.title = "CRM - RueBaRue";
    rentalContext.get();
    settingsContext.get(["tags"]);
  }, []);

  // Keep legacy detail links working, then canonicalize them to a shareable
  // subpage URL such as /crm/owners/42/tasks.
  useEffect(() => {
    if (!idParam || subpage === activeSubpage) return;
    const slug = KEY_TO_SLUG[criteria.category] || categorySlug || "owners";
    history.replace(
      `${buildCrmDetailPath(slug, idParam, activeSubpage)}${location.search || ""}`,
    );
  }, [activeSubpage, categorySlug, criteria.category, history, idParam, location.search, subpage]);

  // Sync activeFilter from criteria.category (handles initial load + URL changes)
  useEffect(() => {
    if (criteria.category && criteria.category !== activeFilter) {
      setActiveFilter(criteria.category);
    }
  }, [criteria.category]);

  // React to URL category changes (back/forward)
  useEffect(() => {
    if (!categorySlug) return;
    const cat = SLUG_TO_KEY[categorySlug];
    if (cat && cat !== criteria.category) {
      setCriteria((c) => ({ ...c, category: cat, page: 0 }));
    }
  }, [categorySlug]);

  useEffect(() => {
    setCriteria((it) => ({ ...it, per: recordsPerPage }));
  }, [recordsPerPage]);

  useDeepCompareEffect(() => {
    const timeoutId = setTimeout(
      () => {
        crmContext.buildCRMList({
          ...criteria,
          units: rentalContext.rentals || [],
        });
      },
      300,
    );
    return () => clearTimeout(timeoutId);
  }, [criteria, rentalContext.rentals]);

  // Auto-select the contact whose share id matches the URL `:id` segment.
  // Only fires when the list refreshes or the URL id changes; doesn't fight
  // user clicks because handleSelectContact already pushes the id.
  useEffect(() => {
    if (!idParam || !listContacts || listContacts.length === 0) return;
    const target = listContacts.find((c) => String(getShareId(c)) === String(idParam));
    if (target && getShareId(selectedContact) !== getShareId(target)) {
      setContact(target);
    }
  }, [idParam, listContacts]);

  // Sync selection flags with contact list length
  useEffect(() => {
    if (selectedFlags.length !== listContacts.length) {
      setSelectedFlags(Array(listContacts.length).fill(false));
    }
  }, [listContacts]);

  // Sync selectedContacts from flags
  useEffect(() => {
    const selected = selectedFlags
      .map((flag, index) => (flag ? listContacts[index] : null))
      .filter((c) => c);
    setSelectedContacts(selected);
    // Mirror to RentalsContext.selectedRentals so unit-level TagsList works
    if (activeFilter === "unit" && rentalContext.setSelectedRentals) {
      rentalContext.setSelectedRentals(selected);
    }
  }, [selectedFlags, listContacts, activeFilter]);

  // Properties & tags for filters
  const propertyValues = useMemo(() => {
    return (rentalContext.rentals || [])
      .filter((r) => r.pms_id)
      .sort((a, b) => (a.pms_id || "").localeCompare(b.pms_id || ""));
  }, [rentalContext.rentals]);

  const tagValues = useMemo(() => {
    return (settingsContext.settings?.tags || []).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [settingsContext.settings]);

  const allTags = useMemo(() => {
    const tagSet = new Set();
    (rentalContext.rentals || []).forEach((r) => {
      (r.tags || []).forEach((t) => tagSet.add(t.name || t));
    });
    return [...tagSet].sort();
  }, [rentalContext.rentals]);

  const { selectedAll, selectedAny } = useMemo(() => {
    const total = listContacts.length;
    const flagsLen = selectedFlags.length;
    const checkedCount = selectedFlags.filter(Boolean).length;
    const selectedAll =
      total > 0 && flagsLen === total && checkedCount === total;
    const selectedAny = checkedCount > 0;
    return { selectedAll, selectedAny };
  }, [selectedFlags, listContacts.length]);

  // ─── Handlers ───
  const handleCriteriaChange = useCallback(
    (key, value) => {
      const newCriteria = { ...criteria, [key]: value, page: key === "page" ? value : 0 };
      if (key === "category") {
        newCriteria.status = "";
        newCriteria.tag = "";
        newCriteria.property = "";
        newCriteria.search_term = "";
        newCriteria.sort = "updated_at";
      }
      setCriteria(newCriteria);
      // Build query string excluding category (now part of the path)
      const { category: _omit, ...rest } = newCriteria;
      const searchParams = new URLSearchParams(
        Object.fromEntries(
          Object.entries(rest).filter(([, v]) => v !== "" && v !== 0),
        ),
      ).toString();
      const slug = KEY_TO_SLUG[newCriteria.category] || "owners";
      // Preserve the selected row id when only filters change; drop it when
      // the category itself changes since the row no longer applies.
      const keepId = key !== "category" ? getShareId(selectedContact) : null;
      const path = buildCrmDetailPath(slug, keepId, activeSubpage);
      history.replace(`${path}${searchParams ? `?${searchParams}` : ""}`);
    },
    [activeSubpage, criteria, history, selectedContact],
  );

  const handleNavClick = useCallback(
    (key, path = null) => {
      if (path) {
        history.push(path);
        return;
      }

      setActiveFilter(key);
      setStatusFilter("all");
      setSelectedFlags([]);
      handleCriteriaChange("category", key);
    },
    [handleCriteriaChange, history, setActiveFilter, setStatusFilter],
  );

  const handleStatusFilter = useCallback(
    (key) => {
      setStatusFilter(key);
      handleCriteriaChange("status", key === "all" ? "" : key);
    },
    [handleCriteriaChange, setStatusFilter],
  );

  const handleSelectContact = useCallback(
    (c) => {
      setContact(c);
      const slug = KEY_TO_SLUG[criteria.category] || "owners";
      const shareId = getShareId(c);
      const search = location.search || "";
      const path = buildCrmDetailPath(slug, shareId, activeSubpage);
      history.replace(`${path}${search}`);
    },
    [activeSubpage, setContact, criteria.category, history, location.search],
  );

  const handleDetailTabChange = useCallback(
    (tab) => {
      const slug = KEY_TO_SLUG[criteria.category] || "owners";
      const shareId = getShareId(selectedContact);
      if (!shareId) return;
      history.push(
        `${buildCrmDetailPath(slug, shareId, tab)}${location.search || ""}`,
      );
    },
    [criteria.category, history, location.search, selectedContact],
  );

  const handleAddContact = useCallback(() => {
    if (activeFilter === "unit") {
      rentalContext.setIsNew(true);
      UIkit.offcanvas("#addPropertyGuide-modal").show();
    } else {
      contactContext.initContact();
      contactContext.setIsNew(true);
      UIkit.offcanvas("#modal-contact-add").show();
    }
  }, [activeFilter, contactContext, rentalContext]);

  const handleContactSaved = useCallback(() => {
    crmContext.buildCRMList({
      ...criteria,
      refreshCounts: true,
      units: rentalContext.rentals || [],
    });
  }, [crmContext, criteria, rentalContext.rentals]);

  const handleSendMessage = useCallback(() => {
    if (!selectedContact) return;
    contactContext.setBatchFlag(false);
    contactContext.setContact(selectedContact);
    UIkit.offcanvas("#modal-contact-message").show();
  }, [contactContext, selectedContact]);

  const handleBatchMessage = useCallback(() => {
    contactContext.setBatchFlag(true);
    contactContext.setSelectedContacts(selectedContacts);
    UIkit.offcanvas("#modal-contact-message").show();
  }, [contactContext, selectedContacts]);

  const handleBatchDelete = useCallback(() => {
    contactContext.setBatchFlag(true);
    contactContext.setSelectedContacts(selectedContacts);
    UIkit.modal("#modal-contact-delete").show();
  }, [contactContext, selectedContacts]);

  const handleDeleteSingle = useCallback(() => {
    if (!selectedContact) return;
    contactContext.setBatchFlag(false);
    contactContext.setContact(selectedContact);
    UIkit.modal("#modal-contact-delete").show();
  }, [contactContext, selectedContact]);

  const handleContactDeleted = useCallback(() => {
    setContact(null);
    crmContext.buildCRMList({ ...criteria, units: rentalContext.rentals || [] });
  }, [crmContext, criteria, rentalContext.rentals, setContact]);

  const handleEditContact = useCallback(() => {
    if (!selectedContact) return;
    if (selectedContact._type === "unit") {
      rentalContext.setIsNew(false);
      rentalContext.setRental(selectedContact);
      UIkit.offcanvas("#addPropertyGuide-modal").show();
      return;
    }
    contactContext.setContact(selectedContact);
    contactContext.setIsNew(false);
    contactContext.setIsEditing(true);
    UIkit.offcanvas("#modal-contact-add").show();
  }, [contactContext, rentalContext, selectedContact]);

  const handleDeleteUnit = useCallback(() => {
    if (!selectedContact || selectedContact._type !== "unit") return;
    rentalContext.setRental(selectedContact);
    UIkit.modal("#modal-property-delete").show();
  }, [rentalContext, selectedContact]);

  const handleIndexChanged = useCallback(
    (newPageIndex) => {
      setSelectedFlags([]);
      handleCriteriaChange("page", newPageIndex);
    },
    [handleCriteriaChange],
  );

  const handleSelect = useCallback((checked, index) => {
    setSelectedFlags((flags) => [
      ...flags.slice(0, index),
      checked,
      ...flags.slice(index + 1),
    ]);
  }, []);

  const handleSelectAll = useCallback(
    (event) => {
      const checked = event.target.checked;
      setSelectedFlags(Array(listContacts.length).fill(checked));
    },
    [listContacts.length],
  );

  // Import CSV
  const handleImport = async (event) => {
    const fileObj = event.target.files && event.target.files[0];
    if (!fileObj) return;
    Papa.parse(fileObj, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        let data = results.data.map((row) => {
          const category = row.category?.trim().toLowerCase();
          return {
            ...row,
            category: CONTACT_IMPORT_CATEGORIES.has(category)
              ? category
              : "other",
          };
        });
        if (!data.length) {
          UIkit.notification("No content", "error");
          return;
        }
        const columns = Object.keys(data[0]);
        const requiredColumns = ["category", "email", "first_name", "last_name", "phone"];
        const missingColumns = requiredColumns.filter((col) => !columns.includes(col));
        if (missingColumns.length) {
          UIkit.notification(`Missing Columns: ${missingColumns.join(", ")}`, "error");
          return;
        }
        const chunkSize = 10;
        const chunks = [];
        for (let i = 0; i < data.length; i += chunkSize) {
          chunks.push(data.slice(i, i + chunkSize));
        }
        try {
          const results = await Promise.all(
            chunks.map((chunk) => crmContext.importContacts(chunk)),
          );
          let totalDuplicates = 0;
          let totalErrors = [];
          results.forEach((result) => {
            totalDuplicates += result?.exists?.length || 0;
            if (result?.errors?.length) totalErrors = totalErrors.concat(result.errors);
          });
          if (totalDuplicates > 0) UIkit.notification(`${totalDuplicates} records already exist.`, "error");
          if (totalErrors.length > 0) UIkit.notification(`Failed: ${totalErrors.length} records.`, "error");
          else if (totalDuplicates === 0) UIkit.notification("Contacts Added Successfully", "success");
          crmContext.buildCRMList({ ...criteria, refreshCounts: true, units: rentalContext.rentals || [] });
        } catch (error) {
          console.error("Error importing contacts:", error);
          UIkit.notification("Error importing contacts. Please try again.", "error");
        }
        event.target.value = "";
      },
    });
  };

  // ─── Which filters to show ───
  const currentFilters = useMemo(() => {
    if (activeFilter === "leads") return LEAD_FILTERS;
    return null;
  }, [activeFilter]);

  const canManageCrm = canEdit("crm.contacts");

  const showTagFilter = activeFilter === "unit";
  const isContactCategory = activeFilter !== "unit" && activeFilter !== "guest";
  const isGuestCategory = activeFilter === "guest";
  const isUnitCategory = activeFilter === "unit";
  const showCheckboxes = isContactCategory || isUnitCategory;

  const displayName = (item) => {
    if (item?.pms_id) return item.pms_id;
    const fullName = `${item?.first_name || ""} ${item?.last_name || ""}`.trim();
    return (
      fullName ||
      item?.display_name ||
      item?.name ||
      item?.email ||
      "Unknown"
    );
  };

  const displaySub = (item) => {
    if (item?.pms_id) return item.address || item.name || "";
    return formatContactPhone(item?.formatted_phone || item?.phone);
  };

  const cleanContactForUpdate = useCallback(
    (item) => {
      const {
        _type,
        thread_id,
        total,
        activities,
        reservations,
        co_owners,
        status,
        formatted_phone,
        crm_identity_id,
        contact_id: _contactId,
        primary_role,
        display_name,
        phone_e164,
        ...rest
      } = item;

      if (!rest.account_id && user?.account_id) {
        rest.account_id = user.account_id;
      }

      return rest;
    },
    [user],
  );

  const handleBulkContactTagUpdate = useCallback(
    (contact) => updateContact(cleanContactForUpdate(contact)),
    [cleanContactForUpdate, updateContact],
  );

  return (
    <div className="crm-layout">
      {/* ─── Left Sidebar ─── */}
      <div className="crm-sidebar">
        <div className="crm-sidebar-header">
          <h3>CRM</h3>
        </div>

        <div className="crm-sidebar-nav">
          {NAV_ITEMS.map((item) => (
            <div key={item.key}>
              <div
                className={`crm-sidebar-item crm-sidebar-child ${activeFilter === item.key ? "is-active" : ""}`}
                onClick={() => handleNavClick(item.key, item.path)}
              >
                <span className="crm-sidebar-item-left">
                  {item.label}
                </span>
                <span className="crm-sidebar-count">
                  {item.path ? "" : counts[item.key] || 0}
                </span>
              </div>
              {item.separator && <div className="crm-sidebar-divider" />}
            </div>
          ))}
        </div>
      </div>

      {/* ─── Contact / Unit List ─── */}
      <div className="crm-list">
        {!isGuestCategory && (
        <div className="crm-list-header">
          {canManageCrm && (
            <button className="uk-button hms-btn hms-red-btn uk-button-small crm-icon-btn" onClick={handleAddContact} uk-tooltip={activeFilter === "unit" ? "Add Property" : "Add Contact"}>
              <FeatherIcon name="plus" size={15} />
            </button>
          )}
          {activeFilter !== "unit" && (
            <button
              className="uk-button hms-btn hms-orange-btn uk-button-small crm-icon-btn"
              disabled={!selectedAny}
              onClick={handleBatchMessage}
              uk-tooltip="Message"
            >
              <FeatherIcon name="send" size={15} />
            </button>
          )}
          {activeFilter === "unit" ? (
            <TagsList
              all={tagValues}
              disabled={!canManageCrm || !selectedAny}
              selected={rentalContext.selectedRentals || []}
              updateItem={(rental) =>
                rentalContext.updateRental(rental.rental_id, rental)
              }
              buttonClassName="uk-button hms-btn hms-white-btn uk-button-small crm-icon-btn"
            />
          ) : (
            <TagsList
              all={tagValues}
              disabled={!canManageCrm || !selectedAny}
              selected={selectedContacts}
              updateItem={handleBulkContactTagUpdate}
              buttonClassName="uk-button hms-btn hms-white-btn uk-button-small crm-icon-btn"
            />
          )}
          <input
            style={{ display: "none" }}
            ref={importRef}
            type="file"
            onChange={handleImport}
            accept=".csv"
          />
          {activeFilter !== "unit" && (
            <>
              {canManageCrm && (
                <>
                  <button
                    className="uk-button hms-btn hms-white-btn uk-button-small crm-icon-btn"
                    onClick={() => importRef.current?.click()}
                    uk-tooltip="Import CSV"
                  >
                    <FeatherIcon name="upload" size={15} />
                  </button>
                  <a
                    className="crm-help-link"
                    href="/samples/contact-import-sample.csv"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      className="info-icon"
                      src="/images/info-icon.svg"
                      uk-tooltip="Click to download sample CSV to bulk import contacts"
                    />
                  </a>
                </>
              )}
            </>
          )}
          {activeFilter !== "unit" && canManageCrm && (
            <button
              className="uk-button hms-btn hms-white-btn uk-button-small crm-icon-btn"
              disabled={!selectedAny}
              onClick={handleBatchDelete}
              uk-tooltip="Delete"
            >
              <FeatherIcon name="trash-2" size={15} />
            </button>
          )}
        </div>
        )}

        <div className="crm-list-search">
          {activeFilter === "unit" ? <input className="crm-search-input" type="search" placeholder="Search properties…"
            value={criteria.search_term} onChange={(event) => handleCriteriaChange("search_term", event.target.value)} />
            : <ContactSearch onSelect={(contact) => {
              const category = contact._type === "guest" ? "guest" : contact.category === "lead" ? "leads" : contact.category;
              setContact(contact);
              setCriteria((previous) => ({ ...previous, category, search_term: "", tag: "", property: "", page: 0 }));
              setSelectedFlags([]);
              history.push(buildCrmDetailPath(KEY_TO_SLUG[category] || "other", getShareId(contact), "overview"));
            }} />}
        </div>

        {/* Property & Tag & Sort filters for contacts */}
        {isContactCategory && (
          <div className="crm-list-filters">
            {activeFilter !== "leads" && (
              <SearchableDropdown
                value={criteria.property}
                onChange={(val) => handleCriteriaChange("property", val)}
                placeholder="All Properties"
                options={[
                  { value: "", label: "All Properties" },
                  ...propertyValues.map((p) => ({
                    value: p.pms_id,
                    label: p.pms_id,
                  })),
                ]}
              />
            )}
            <SearchableDropdown
              value={criteria.tag}
              onChange={(val) => handleCriteriaChange("tag", val)}
              placeholder="All Tags"
              options={[
                { value: "", label: "All Tags" },
                ...tagOptionsByGroup(
                  tagValues,
                  settingsContext.settings.tag_groups,
                  (tag) => tag.name,
                ),
              ]}
            />
          </div>
        )}

        {/* Sub-filters: only for lead_owner and lead_guest */}

        {/* Tag filter for Properties */}
        {showTagFilter && allTags.length > 0 && (
          <div className="crm-list-filters">
            <SearchableDropdown
              value={criteria.tag}
              onChange={(val) => handleCriteriaChange("tag", val)}
              placeholder="Filter by tag"
              inline
              options={[
                { value: "", label: "All tags" },
                ...allTags.map((t) => ({ value: t, label: t })),
              ]}
            />
          </div>
        )}

        {/* Select All checkbox */}
        {(showCheckboxes || isGuestCategory) && !flagIsProcessing && (
          <div className="crm-list-select-all">
            {showCheckboxes && listContacts.length > 0 ? (
              <label className="crm-checkbox-label">
                <input
                  type="checkbox"
                  checked={selectedAll}
                  onChange={handleSelectAll}
                />
                <span className="crm-checkbox-text">Select All</span>
              </label>
            ) : <div />}

            <div className="hms-sortby-btn">
              <button className="uk-button uk-button-small" type="button">
                Sort<span uk-icon="icon: triangle-down; ratio: 0.7"></span>
              </button>
              <div uk-dropdown="mode: click; offset: 0; animation: uk-animation-slide-top-small; duration: 400">
                <ul className="uk-nav uk-dropdown-nav">
                  {(isUnitCategory ? unitSortOptions : sortOptions).map((option, idx) => (
                    <li
                      className={classnames({
                        "uk-active": criteria.sort === option.key,
                      })}
                      key={`sort_option_${idx}`}
                    >
                      <Link
                        href="#"
                        onClick={() =>
                          handleCriteriaChange("sort", option.key)
                        }
                      >
                        {option.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        <div className="crm-list-body">
          {flagIsProcessing && <TableLoading />}

          {!flagIsProcessing && listContacts.length === 0 && (
            <div className="crm-list-empty">
              {activeFilter === "unit" ? "No properties found" : activeFilter === "guest" ? "No guests found" : "No contacts found"}
            </div>
          )}

          {!flagIsProcessing &&
            listContacts.map((c, index) => {
              const rowKey = c.id || c.rental_id || c.guest_id || c.contact_id;
              const selectedKey =
                selectedContact?.id ||
                selectedContact?.rental_id ||
                selectedContact?.guest_id ||
                selectedContact?.contact_id;
              const isSelected = !!rowKey && !!selectedKey && rowKey === selectedKey;
              return (
              <div
                key={rowKey || `row-${index}`}
                className={`crm-contact-row ${isSelected ? "is-selected" : ""}`}
              >
                {showCheckboxes && (
                  <input
                    type="checkbox"
                    className="crm-row-checkbox"
                    checked={selectedFlags[index] || false}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleSelect(e.target.checked, index);
                    }}
                  />
                )}
                <div
                  className="crm-contact-row-inner"
                  onClick={() => handleSelectContact(c)}
                >
                  {c._type === "unit" ? (
                    <div className="crm-contact-icon">
                      <img
                        src="/images/rental.svg"
                        alt=""
                        width="30"
                        height="30"
                      />
                    </div>
                  ) : (
                    <div className="crm-contact-icon">
                      <img
                        src={`/images/${c.category || "other"}-active.svg`}
                        alt=""
                        width="30"
                        height="30"
                      />
                    </div>
                  )}
                  <div className="crm-contact-info">
                    <div className="crm-contact-name">{displayName(c)}</div>
                    {displaySub(c) && (
                      <div className="crm-contact-sub">{displaySub(c)}</div>
                    )}
                  </div>
                </div>
              </div>
              );
            })}
        </div>

        <div className="crm-list-footer">
          {totalCnt > recordsPerPage && (
            <Pagination
              index={criteria.page}
              count={totalCnt}
              perPage={recordsPerPage}
              onIndexChanged={handleIndexChanged}
            />
          )}
          <RecordsPerPage />
        </div>
      </div>

      {/* ─── Detail Panel ─── */}
      {selectedContact ? (
        <CRMDetail
          contact={selectedContact}
          activeFilter={activeFilter}
          activeTab={activeSubpage}
          onTabChange={handleDetailTabChange}
          onSendMessage={handleSendMessage}
          onEdit={canManageCrm ? handleEditContact : null}
          onDelete={canManageCrm ? (selectedContact._type === "unit" ? handleDeleteUnit : handleDeleteSingle) : null}
          onRefresh={() => crmContext.buildCRMList({ ...criteria, units: rentalContext.rentals || [] })}
          readOnly={!canManageCrm}
        />
      ) : (
        <div className="crm-detail">
          <div className="crm-detail-empty">
            {activeFilter === "unit"
              ? "Select a property to view details"
              : "Select a contact to view details"}
          </div>
        </div>
      )}

      {/* ─── Reuse the existing Contacts Add off-canvas ─── */}
      {canManageCrm && <ContactAdd onSaved={handleContactSaved} />}

      {/* ─── Add Property off-canvas ─── */}
      {canManageCrm && <RentalFormModal config={ADD_UNIT_CRM_CONFIG} />}

      {/* ─── Reuse the existing Contacts Message off-canvas ─── */}
      <ContactMessage />

      {/* ─── Reuse the existing Contacts Delete dialog ─── */}
      {canManageCrm && <ContactDelete onDeleted={handleContactDeleted} />}

      {/* ─── Property Delete dialog ─── */}
      {canManageCrm && (
        <DeleteRentalDialog
          config={{
            title: "Property",
            onDeleted: () => {
              setContact(null);
              crmContext.buildCRMList({
                ...criteria,
                units: rentalContext.rentals || [],
              });
            },
          }}
        />
      )}

      {/* ─── Activity Off-Canvas ─── */}
      <CRMActivityAdd />
    </div>
  );
};

export default CRM;
